// Vendor libraries
import { ElementRef, NgZone } from '@angular/core';
import Sortable from 'sortablejs';
import cloneDeep from 'lodash.clonedeep';
// Utils
import { disablePointerEvents, enablePointerEvents } from 'NewAngular/utils/sortable-utils';
import { uniqueBy } from 'NewAngular/utils/array-utils';
// Constants
import { EDIT_TALKING_POINTS_FEATURE } from 'EntitlementsApp/ts/features/feature-needed/feature-needed.constants';
// Services
import { FeatureService } from 'EntitlementsApp/ts/features/feature-needed/feature.service';
import { FolderableCollectionServiceBase } from 'FoldersApp/services/folderable-collection-service-base.service';
// Components
import { TalkingPointsEditModalComponent } from 'TalkingPointsApp/features/talking-points-edit-modal/talking-points-edit-modal.component';
import { TalkingPointTextEditModalComponent } from 'NewAngular/features/components/modals/talking-point-text-edit-modal/talking-point-text-edit-modal.component';
import * as i0 from "@angular/core";
import * as i1 from "EntitlementsApp/ts/features/feature-needed/feature.service";
import * as i2 from "FoldersApp/services/folderable-collection-service-base.service";
const _c0 = ["selectedPointsContainer"];
const _c1 = ["talkingPointTextEditModal"];
const _c2 = ["talkingPointsEditModal"];
/**
 * Edit Talking Points Modal Directive
 *
 * Holds common properties and logic for modals that handle selecting and editing talking point
 * for a PDF report. Meant to be used with edit-talking-points-modal.template.html.
 *
 * Input Properties:
 *  talkingPointsDataMap: map of Talking Points data to corresponding ids.
 *
 */
export class EditTalkingPointsModalDirective {
    constructor(ngZone, featureService, folderableCollectionService) {
        this.ngZone = ngZone;
        this.featureService = featureService;
        this.folderableCollectionService = folderableCollectionService;
        this.selectedTalkingPoints = [];
        this.talkingPoints = [];
        this.reportDefaultOnly = false;
        // Folders
        this.targetFolder = null;
        this.hasEditFeature = this.featureService.userHasFeatures([EDIT_TALKING_POINTS_FEATURE]);
        this.infoMessage = '';
    }
    ngAfterViewInit() {
        this.ngZone.runOutsideAngular(() => {
            Sortable.create(this.selectedPointsContainer.nativeElement, {
                handle: '.btn-reorder',
                scroll: false,
                animation: 150,
                onUpdate: (event) => {
                    this.ngZone.run(() => {
                        this.selectedTalkingPoints.splice(event.newIndex, 0, this.selectedTalkingPoints.splice(event.oldIndex, 1)[0]);
                    });
                },
                onStart: () => {
                    disablePointerEvents(document, 'plate-container');
                },
                onEnd: () => {
                    enablePointerEvents(document, 'plate-container');
                },
            });
        });
    }
    closeModal() {
        this.modalOpen = false;
        this.infoMessage = '';
        this.selectedTalkingPoints = [];
        this.talkingPoints = [];
    }
    addTalkingPointFromAutocomplete(newTalkingPoint) {
        if (!this.hasMax || this.selectedTalkingPoints.length < 2) {
            const tpId = newTalkingPoint.id.toString();
            // There can be a lag between the autocomplete results and the db state.
            // If the talking point no longer exists, do not add it.
            if (!(tpId in this.talkingPointsDataMap)) {
                this.infoMessage =
                    'There was an issue adding the selected talking point. Please select a different talking point.';
                return;
            }
            this.selectedTalkingPoints.push({
                talkingPoint: cloneDeep(this.talkingPointsDataMap[tpId]),
                showMore: false,
            });
            this.selectedTalkingPoints = uniqueBy(this.selectedTalkingPoints, (element) => element.talkingPoint.id);
            const index = this.talkingPoints.findIndex((_) => _.talkingPoint.id === newTalkingPoint.id);
            if (index !== -1) {
                this.talkingPoints.splice(index, 1);
            }
        }
        this.infoMessage = '';
    }
    addTalkingPoint(id) {
        if (!this.hasMax || this.selectedTalkingPoints.length < 2) {
            const index = this.talkingPoints.findIndex((_) => _.talkingPoint.id === id);
            const added = this.talkingPoints.splice(index, 1);
            added[0].showMore = true;
            this.selectedTalkingPoints.push(added[0]);
        }
        this.infoMessage = '';
    }
    removeTalkingPoint(id) {
        const index = this.selectedTalkingPoints.findIndex((_) => _.talkingPoint.id === id);
        const removed = this.selectedTalkingPoints.splice(index, 1);
        this.talkingPoints.push(removed[0]);
        this.infoMessage = '';
    }
    editTalkingPoint(talkingPoint) {
        this.talkingPointTextEditModalTitle = `Edit ${talkingPoint.name} Text`;
        this.talkingPointToBeEdited = talkingPoint;
        this.talkingPointTextEditModal.openModal();
    }
    textSaved(newText) {
        this.talkingPointToBeEdited.proposal_text = newText;
    }
    createTalkingPoint(talkingPoint) {
        this.talkingPointsDataMap[talkingPoint.id] = talkingPoint;
        this.selectedTalkingPoints.push({ talkingPoint, showMore: true });
        this.modalOpen = true;
    }
    createClickHandler() {
        this.closeModal();
        if (this.customTalkingPointsEditModal) {
            this.customTalkingPointsEditModal.openModal();
        }
        else {
            this.talkingPointsEditModal.openModal();
        }
    }
}
EditTalkingPointsModalDirective.ɵfac = function EditTalkingPointsModalDirective_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || EditTalkingPointsModalDirective)(i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i1.FeatureService), i0.ɵɵdirectiveInject(i2.FolderableCollectionServiceBase)); };
EditTalkingPointsModalDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: EditTalkingPointsModalDirective, viewQuery: function EditTalkingPointsModalDirective_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(_c0, 5);
        i0.ɵɵviewQuery(_c1, 5);
        i0.ɵɵviewQuery(_c2, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.selectedPointsContainer = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.talkingPointTextEditModal = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.talkingPointsEditModal = _t.first);
    } }, inputs: { talkingPointsDataMap: "talkingPointsDataMap", customTalkingPointsEditModal: "customTalkingPointsEditModal" } });
